import { describeError, useApi } from '@classgo/app-kit';
import { Badge, Button, Card, EmptyState, Input, Modal, Table, Tabs, useToast } from '@classgo/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { ActAsButton } from '../../act-as';
import { staffApi, staffQueries } from '../../api/staff-api';
import type { ContractView, TenantDetail, TenantUsers } from '../../api/types';
import { ContractForm } from '../../forms/contract-form';
import { PersonForm } from '../../forms/person-form';
import { SchoolForm } from '../../forms/school-form';
import { useT } from '../../i18n';
import {
  contractFormToCreate,
  contractFormToUpdate,
  contractToForm,
  emptyContractForm,
} from '../../lib/contract-form';
import { formatDate, formatDateTime, formatMoney, today } from '../../lib/format';

const Overview = ({ tenant }: { tenant: TenantDetail }) => {
  const t = useT();
  const api = useApi();
  const health = useQuery(staffQueries.health(api, tenant.id));
  const row = (label: string, value: string | null) => (
    <div>
      <dt className="text-gray-500">{label}</dt>
      <dd className="mt-0.5 font-medium text-gray-900">{value || t('common.none')}</dd>
    </div>
  );
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title={t('tenant.identity.title')}>
        <dl className="grid gap-4 text-sm sm:grid-cols-2">
          {row(t('tenant.field.name'), tenant.name)}
          {row(t('tenant.field.legalName'), tenant.legalName)}
          {row(t('tenant.field.nit'), tenant.nit)}
          {row(t('tenant.field.daneCode'), tenant.daneCode)}
          {row(
            t('tenant.field.defaults'),
            `${tenant.countryCode} · ${tenant.currency} · ${tenant.locale} · ${tenant.timezone}`,
          )}
          {row(t('tenants.column.created'), formatDate(tenant.createdAt))}
        </dl>
        <h3 className="mt-6 text-sm font-semibold text-gray-900">{t('tenant.sedes.title')}</h3>
        <ul className="mt-2 space-y-1 text-sm text-gray-700">
          {tenant.sedes.map((sede) => (
            <li key={sede.id}>
              {sede.name}
              {sede.isMain ? ` (${t('tenant.sedes.main')})` : ''}
            </li>
          ))}
        </ul>
      </Card>
      <Card title={t('tenant.health.title')}>
        {health.data ? (
          <dl className="grid gap-4 text-sm sm:grid-cols-2">
            {row(t('tenant.health.users'), String(health.data.users))}
            {row(t('tenant.health.sedes'), String(health.data.sedes))}
            {row(t('tenant.health.groups'), String(health.data.groups))}
            {row(
              t('tenant.health.activeContract'),
              health.data.activeContract
                ? `${formatDate(health.data.activeContract.startsOn)} · ${formatMoney(health.data.activeContract.platformFee)} + ${formatMoney(health.data.activeContract.pricePerStudent)} ${t('contract.perStudent')}`
                : t('tenant.health.none'),
            )}
            {row(
              t('tenant.health.lastActivity'),
              health.data.lastActivityAt
                ? formatDateTime(health.data.lastActivityAt)
                : t('tenant.health.never'),
            )}
            {row(
              t('tenant.health.people'),
              t('tenant.health.people.value', {
                students: health.data.people.students,
                guardians: health.data.people.guardians,
                teachers: health.data.people.teachers,
              }),
            )}
            {row(
              t('tenant.health.setup'),
              health.data.setup.ready
                ? t('tenant.health.setup.ready')
                : t('tenant.health.setup.value', {
                    percent: health.data.setup.percent,
                    pending: health.data.setup.pending,
                  }),
            )}
            {row(
              t('tenant.health.lastImport'),
              health.data.lastImport
                ? `${formatDateTime(health.data.lastImport.finishedAt ?? health.data.lastImport.createdAt)} · ${t(`tenant.health.import.${health.data.lastImport.status}`)} · ${health.data.lastImport.rows} ${t('tenant.health.importRows')}${health.data.lastImport.errors > 0 ? `, ${health.data.lastImport.errors} ${t('tenant.health.importErrors')}` : ''}`
                : t('tenant.health.noImport'),
            )}
          </dl>
        ) : (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        )}
      </Card>
    </div>
  );
};

const Users = ({ tenant }: { tenant: TenantDetail }) => {
  const t = useT();
  const api = useApi();
  const users = useQuery(staffQueries.users(api, tenant.id));
  const roleLabel = (roles: string[]) =>
    roles.map((role) => t(`tenant.users.role.${role}` as never)).join(', ');
  return (
    <Card title={t('tenant.users.title')} flush>
      {users.data ? (
        <p className="px-4 pt-4 text-sm text-gray-600">
          {t('tenant.users.counts', {
            owners: users.data.counts.owners,
            staff: users.data.counts.staff,
            teachers: users.data.counts.teachers,
            students: users.data.counts.students,
            guardians: users.data.counts.guardians,
          })}
        </p>
      ) : null}
      <Table
        caption={t('tenant.users.caption')}
        rows={users.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={users.isPending}
        empty={<EmptyState title={t('tenant.users.empty')} />}
        columns={[
          {
            key: 'name',
            header: t('tenant.users.column.name'),
            cell: (row) => (
              <div>
                <p className="font-medium text-gray-900">
                  {row.familyName}, {row.givenName}
                </p>
                <p className="text-xs text-gray-500">{row.email}</p>
              </div>
            ),
          },
          {
            key: 'roles',
            header: t('tenant.users.column.roles'),
            cell: (row) => roleLabel(row.roles),
          },
          {
            key: 'account',
            header: t('tenant.users.column.account'),
            cell: (row) => <AccessBadge person={row} />,
          },
        ]}
      />
    </Card>
  );
};

const Contracts = ({ tenant }: { tenant: TenantDetail }) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ContractView | null>(null);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['staff', 'tenants', tenant.id] });
  };
  const period = (contract: ContractView) =>
    `${formatDate(contract.startsOn)} → ${contract.endsOn ? formatDate(contract.endsOn) : t('contract.open')}`;
  const newButton = <Button onClick={() => setCreating(true)}>{t('contract.new')}</Button>;
  return (
    <>
      <Card title={t('contract.title')} actions={newButton} flush>
        <Table<ContractView>
          caption={t('contract.caption')}
          rows={tenant.contracts}
          rowKey={(row) => row.id}
          onRowClick={(row) => setEditing(row)}
          empty={<EmptyState title={t('contract.empty')} action={newButton} />}
          columns={[
            { key: 'period', header: t('contract.column.period'), cell: period },
            {
              key: 'cycle',
              header: t('contract.column.cycle'),
              cell: (row) =>
                row.billingCycle === 'annual'
                  ? t('contract.cycle.annual')
                  : t('contract.cycle.monthly'),
            },
            {
              key: 'fees',
              header: t('contract.column.fees'),
              cell: (row) => (
                <span>
                  {formatMoney(row.platformFee, row.currency)} +{' '}
                  {formatMoney(row.pricePerStudent, row.currency)} {t('contract.perStudent')}
                  {row.discountPercent > 0 ? ` · −${row.discountPercent}%` : ''}
                  {row.mentoriaEnabled ? ` · ${t('contract.mentoria')}` : ''}
                </span>
              ),
            },
            {
              key: 'courtesy',
              header: t('contract.column.courtesy'),
              cell: (row) =>
                row.courtesyStartsOn && row.courtesyEndsOn
                  ? `${formatDate(row.courtesyStartsOn)} → ${formatDate(row.courtesyEndsOn)}`
                  : t('contract.noCourtesy'),
            },
            {
              key: 'status',
              header: t('contract.column.status'),
              cell: (row) => (
                <Badge tone={row.status === 'active' ? 'success' : 'gray'}>
                  {row.status === 'active'
                    ? t('contract.status.active')
                    : t('contract.status.ended')}
                </Badge>
              ),
            },
          ]}
        />
      </Card>
      <Modal open={creating} onClose={() => setCreating(false)} title={t('contract.new')} size="lg">
        <ContractForm
          initial={emptyContractForm(today())}
          mode="create"
          submitLabel={t('common.save')}
          onSubmit={async (values) => {
            await staffApi(api).createContract(tenant.id, contractFormToCreate(values));
            await refresh();
            setCreating(false);
            toast.show({ tone: 'success', title: t('contract.created') });
          }}
        />
      </Modal>
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={t('contract.edit')}
        size="lg"
      >
        {editing ? (
          <ContractForm
            initial={contractToForm(editing)}
            mode="edit"
            submitLabel={t('common.save')}
            onSubmit={async (values) => {
              await staffApi(api).updateContract(
                tenant.id,
                editing.id,
                contractFormToUpdate(values),
              );
              await refresh();
              setEditing(null);
              toast.show({ tone: 'success', title: t('contract.updated') });
            }}
          />
        ) : null}
      </Modal>
    </>
  );
};

type TenantPerson = TenantUsers['items'][number];

/**
 * Where a person of the school stands with their access, in one badge: first sign-in pending,
 * active, invitation pending or expired, or no account.
 */
const AccessBadge = ({ person }: { person: TenantPerson }) => {
  const t = useT();
  if (person.firstLoginPending) {
    return <Badge tone="info">{t('tenant.users.firstLoginPending')}</Badge>;
  }
  if (person.hasAccount) {
    return <Badge tone="success">{t('tenant.users.account.yes')}</Badge>;
  }
  if (person.invitation) {
    return (
      <Badge tone={person.invitation.status === 'pending' ? 'info' : 'warning'}>
        {t(`tenant.users.invitation.${person.invitation.status}`)}
      </Badge>
    );
  }
  return <Badge tone="gray">{t('tenant.users.account.no')}</Badge>;
};

const People = ({ tenant }: { tenant: TenantDetail }) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const users = useQuery(staffQueries.users(api, tenant.id));
  const owners = (users.data?.items ?? []).filter((person) =>
    person.roles.includes('SCHOOL_OWNER'),
  );
  const [invited, setInvited] = useState<string | null>(null);
  const [editing, setEditing] = useState<TenantPerson | null>(null);
  const [names, setNames] = useState({ givenName: '', familyName: '' });
  const [removing, setRemoving] = useState<TenantPerson | null>(null);
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['staff', 'tenants', tenant.id] });
  const fullName = (person: TenantPerson) =>
    [person.givenName, person.familyName].filter(Boolean).join(' ') || (person.email ?? '');

  const save = useMutation({
    mutationFn: (person: TenantPerson) =>
      staffApi(api).updateOwner(tenant.id, person.id, {
        givenName: names.givenName.trim(),
        familyName: names.familyName.trim(),
      }),
    onSuccess: async () => {
      toast.show({ tone: 'success', title: t('owner.edit.saved') });
      setEditing(null);
      await refresh();
    },
    onError: (error) =>
      toast.show({
        tone: 'error',
        title: t('owner.edit.title'),
        description: describeError(t, error),
      }),
  });
  const remove = useMutation({
    mutationFn: (person: TenantPerson) => staffApi(api).removeOwner(tenant.id, person.id),
    onSuccess: async (_, person) => {
      toast.show({
        tone: 'success',
        title: t('owner.remove.done', { name: fullName(person) }),
      });
      setRemoving(null);
      await refresh();
    },
    onError: (error) =>
      toast.show({
        tone: 'error',
        title: t('owner.remove.title'),
        description: describeError(t, error),
      }),
  });
  const startEdit = (person: TenantPerson) => {
    setNames({ givenName: person.givenName ?? '', familyName: person.familyName ?? '' });
    setEditing(person);
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title={t('owner.current.title')} flush>
        <Table<TenantPerson>
          caption={t('owner.current.caption')}
          rows={owners}
          rowKey={(row) => row.id}
          loading={users.isPending}
          empty={<EmptyState title={t('owner.current.empty')} />}
          columns={[
            {
              key: 'name',
              header: t('tenant.users.column.name'),
              cell: (row) => (
                <div>
                  <p className="font-medium text-gray-900">{fullName(row)}</p>
                  <p className="break-all text-xs text-gray-500">{row.email}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <AccessBadge person={row} />
                    {row.hasAccount && !row.firstLoginPending ? (
                      <Badge tone={row.mfaEnrolled ? 'success' : 'warning'}>
                        {t(row.mfaEnrolled ? 'owner.mfa.yes' : 'owner.mfa.no')}
                      </Badge>
                    ) : null}
                  </div>
                </div>
              ),
            },
            {
              key: 'actions',
              header: t('owner.column.actions'),
              align: 'right',
              cell: (row) => (
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t('owner.edit.for', { name: fullName(row) })}
                    onClick={() => startEdit(row)}
                  >
                    {t('owner.edit')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t('owner.remove.for', { name: fullName(row) })}
                    onClick={() => setRemoving(row)}
                  >
                    {t('owner.remove')}
                  </Button>
                </div>
              ),
            },
          ]}
        />
        {owners.some((owner) => owner.firstLoginPending) ? (
          <p className="px-4 pb-4 text-sm text-gray-500 md:px-6">
            {t('owner.current.pendingHelp')}
          </p>
        ) : null}
      </Card>
      <Card title={owners.length > 0 ? t('owner.another') : t('owner.title')}>
        <p className="mb-4 text-sm text-gray-500">{t('owner.intro')}</p>
        {invited ? (
          <p
            role="status"
            className="mb-4 rounded-control bg-success-100 px-3 py-2 text-sm text-success-700"
          >
            {t('owner.created', { email: invited })}
          </p>
        ) : null}
        <PersonForm
          kind="owner"
          submitLabel={t('owner.invite')}
          onSubmit={async (values) => {
            const email = values.email.trim().toLowerCase();
            await staffApi(api).createOwner(tenant.id, {
              email,
              ...(values.givenName.trim() ? { givenName: values.givenName.trim() } : {}),
              ...(values.familyName.trim() ? { familyName: values.familyName.trim() } : {}),
            });
            await refresh();
            setInvited(email);
          }}
        />
      </Card>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={t('owner.edit.title')}
        actions={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="owner-edit-form" loading={save.isPending}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        {editing ? (
          <form
            id="owner-edit-form"
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (names.givenName.trim() && names.familyName.trim()) save.mutate(editing);
            }}
          >
            <Input
              label={t('owner.field.email')}
              value={editing.email ?? ''}
              disabled
              help={t('owner.edit.emailHelp')}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label={t('owner.field.givenName')}
                required
                value={names.givenName}
                onChange={(event) => setNames({ ...names, givenName: event.target.value })}
              />
              <Input
                label={t('owner.field.familyName')}
                required
                value={names.familyName}
                onChange={(event) => setNames({ ...names, familyName: event.target.value })}
              />
            </div>
          </form>
        ) : null}
      </Modal>

      <Modal
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={t('owner.remove.title')}
        actions={
          <>
            <Button variant="secondary" onClick={() => setRemoving(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => removing && remove.mutate(removing)}
            >
              {t('owner.remove')}
            </Button>
          </>
        }
      >
        {removing ? (
          <div className="space-y-3 text-sm text-gray-700">
            <p>
              {t('owner.remove.body', { name: fullName(removing), email: removing.email ?? '' })}
            </p>
            {owners.length === 1 ? (
              <p className="rounded-control bg-warning-100 px-3 py-2 text-warning-700">
                {t('owner.remove.last')}
              </p>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

/**
 * One school: identity, health, contracts and the owner invitation, plus act-as, suspension and
 * edits. Everything the console does to a school after onboarding happens here.
 */
export const TenantDetailScreen = () => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { tenantId = '' } = useParams({ strict: false });
  const { data: tenant } = useSuspenseQuery(staffQueries.tenant(api, tenantId));
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState('overview');

  const update = useMutation({
    mutationFn: (input: Parameters<ReturnType<typeof staffApi>['updateTenant']>[1]) =>
      staffApi(api).updateTenant(tenant.id, input),
    onSuccess: async (_, input) => {
      await queryClient.invalidateQueries({ queryKey: ['staff', 'tenants'] });
      toast.show({
        tone: 'success',
        title:
          input.status === 'suspended'
            ? t('tenant.suspended')
            : input.status === 'active'
              ? t('tenant.reactivated')
              : t('tenant.saved'),
      });
    },
    onError: (thrown) => toast.show({ tone: 'error', title: describeError(t, thrown) }),
  });
  const suspended = tenant.status !== 'active';

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">{tenant.name}</h2>
            <div className="mt-2 flex flex-wrap gap-1">
              <Badge tone={suspended ? 'warning' : 'success'}>
                {suspended ? t('tenant.status.suspended') : t('tenant.status.active')}
              </Badge>
              {tenant.isDemo ? <Badge tone="info">{t('tenant.demo')}</Badge> : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <ActAsButton tenant={tenant} />
            <Button variant="secondary" onClick={() => setEditing(true)}>
              {t('tenant.actions.edit')}
            </Button>
            <Button
              variant={suspended ? 'primary' : 'danger'}
              loading={update.isPending}
              onClick={() => update.mutate({ status: suspended ? 'active' : 'suspended' })}
            >
              {suspended ? t('tenant.actions.reactivate') : t('tenant.actions.suspend')}
            </Button>
          </div>
        </div>
      </Card>
      <Tabs
        label={t('tenant.tabs.label')}
        value={tab}
        onChange={setTab}
        items={[
          {
            id: 'overview',
            label: t('tenant.tabs.overview'),
            content: <Overview tenant={tenant} />,
          },
          {
            id: 'contracts',
            label: t('tenant.tabs.contracts'),
            content: <Contracts tenant={tenant} />,
          },
          { id: 'users', label: t('tenant.tabs.users'), content: <Users tenant={tenant} /> },
          { id: 'people', label: t('tenant.tabs.people'), content: <People tenant={tenant} /> },
        ]}
      />
      <Modal
        open={editing}
        onClose={() => setEditing(false)}
        title={t('tenant.editTitle')}
        size="lg"
      >
        <SchoolForm
          initial={{
            name: tenant.name,
            legalName: tenant.legalName ?? '',
            nit: tenant.nit ?? '',
            daneCode: tenant.daneCode ?? '',
            mainSedeName: '',
            isDemo: tenant.isDemo,
          }}
          mode="edit"
          submitLabel={t('common.save')}
          onSubmit={async (values) => {
            await update.mutateAsync({
              name: values.name.trim(),
              legalName: values.legalName.trim() || null,
              nit: values.nit.trim() || null,
              daneCode: values.daneCode.trim() || null,
              isDemo: values.isDemo,
            });
            setEditing(false);
          }}
        />
      </Modal>
    </div>
  );
};
